/**
 * On-device selections for a signed-in user's DRAFT ballots. A draft's picks
 * stay on the device (localStorage) and reach Cosmos only when the ballot is
 * saved — mirroring the guest experience, so "Draft" means "not committed yet."
 *
 * Keyed per ballot id so each draft (including per-election tabs at one address)
 * keeps its own picks. Listed in PERSISTENT_KEYS so it survives the beforeunload
 * refresh wipe, and is cleared on explicit sign-out with the rest of VoteMate
 * storage.
 */
const STORAGE_KEY = 'votemate_draft_selections';

type DraftMap = Record<string, string[]>;

const read = (): DraftMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as DraftMap;
  } catch {
    return {};
  }
};

const write = (map: DraftMap): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // storage full / disabled (private mode, quota) — best-effort.
  }
};

/** On-device picks for a ballot, or ``null`` when this device has no working
 * copy (so callers can fall back to the last-saved Cosmos selections). An empty
 * array means the user edited the ballot down to zero picks — distinct from null. */
export const getDraftSelections = (ballotId: string): string[] | null => {
  const map = read();
  if (!(ballotId in map)) return null;
  const ids = map[ballotId];
  return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string') : [];
};

export const setDraftSelections = (ballotId: string, ids: string[]): void => {
  const map = read();
  map[ballotId] = Array.from(new Set(ids));
  write(map);
};

export const clearDraftSelections = (ballotId: string): void => {
  const map = read();
  if (ballotId in map) {
    delete map[ballotId];
    write(map);
  }
};

/** Drop cache entries for ballots that no longer exist (e.g. after deletion). */
export const pruneDraftSelections = (validIds: string[]): void => {
  const map = read();
  const valid = new Set(validIds);
  let changed = false;
  for (const id of Object.keys(map)) {
    if (!valid.has(id)) {
      delete map[id];
      changed = true;
    }
  }
  if (changed) write(map);
};
